// Lib
import chroma from 'chroma-js';
import { Thunk } from '../../../../../types/reduxTypes';
import Immutable from 'immutable';

// Actions
import { getNewElementId } from '../../../../../element/actions/elementActions';
import { addRootToLabel, createLabel } from '../../../../../../common/labels/labelActions';

// Types
import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';
import { Label, LabelCreationData } from '../../../../../../common/labels/labelTypes';
import { getCurrentContextRootId } from './labelUtils';
import {
    addLabelToElements,
    removeLabelFromElements,
    replaceLabelOnElements,
} from '../../../../../element/labels/elementLabelActions';
import { getElements } from '../../../../../element/selectors/elementSelector';
import { getElement } from '../../../../../../common/elements/utils/elementTraversalUtils';
import { getNewTransactionId } from '../../../../../utils/undoRedo/undoRedoTransactionManager';

export const createNewLabelThunk =
    (labelData: LabelCreationData, transactionId?: number): Thunk<Label> =>
    (dispatch: Function) => {
        const id = dispatch(getNewElementId());

        const { name, creator, rootBoardIds, color } = labelData;
        const label = {
            id,
            name,
            rootBoardIds,
            color: color || chroma.random().hex(),
            meta: {
                creator,
                createdTime: Date.now(),
                modifiedTime: Date.now(),
            },
        };

        dispatch(createLabel(label, transactionId));
        return label;
    };

export const addLabelToElementsThunk =
    (
        label: Label,
        elements: Immutable.List<ImMNElement>,
        currentBoardId: string,
        shareRootPath: ImMNElement[],
        transactionId?: number,
    ): Thunk =>
    (dispatch: Function) => {
        if (!getCurrentContextRootId(label, shareRootPath)) {
            dispatch(addRootToLabel(label, currentBoardId));
        }

        dispatch(addLabelToElements(elements, label.id, transactionId));
    };

export const createAndAddLabelToElementsThunk =
    (
        labelData: LabelCreationData,
        elements: Immutable.List<ImMNElement>,
        currentBoardId: string,
        shareRootPath: ImMNElement[],
    ): Thunk =>
    (dispatch: Function) => {
        const sharedTransactionId = getNewTransactionId();
        const newLabel = dispatch(createNewLabelThunk(labelData, sharedTransactionId));
        dispatch(addLabelToElementsThunk(newLabel, elements, currentBoardId, shareRootPath, sharedTransactionId));
    };

export const replaceLabelIdForElementsThunk =
    (elementIds: string[], oldLabelId: string, newLabelId?: string): Thunk =>
    (dispatch: Function, getState: Function) => {
        const state = getState();
        const elements = getElements(state);

        const updatingElements = Immutable.List(elementIds.map((id) => getElement(elements, id)));
        if (!updatingElements || updatingElements.size === 0) return;

        if (!newLabelId) {
            // If no new label id is provided, we are just removing the old label from the elements
            return dispatch(removeLabelFromElements(updatingElements, oldLabelId));
        }

        dispatch(replaceLabelOnElements(updatingElements, oldLabelId, newLabelId));
    };
